/**
 * Data Center Editor Page — интерактивный 2D-редактор ЦОДа
 *
 * Слои (снизу вверх):
 *   GridLayer → TraysLayer → RacksLayer → LensLayer → OverlayLayer
 *
 * Инструменты: Select (V), Draw Tray (D), Erase (E)
 * Горячие клавиши: Ctrl+Z / Ctrl+Y, Ctrl+A, Ctrl+D, Ctrl+S, Delete, Escape
 * Режимы: Черновик (auto-save) → Публикация (атомарно обновляет rack_configs)
 */

import { useRef, useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DataCenterCanvas } from '../components/editor/DataCenterCanvas'
import { EditorToolbar } from '../components/editor/EditorToolbar'
import { ObjectPalette } from '../components/editor/ObjectPalette'
import { useEditorStore } from '../store/editorStore'
import { useDataCenterEditor } from '../hooks/useDataCenterEditor'
import { DraftsSidebar } from '../components/editor/DraftsSidebar'
import { PropertiesSidebar } from '../components/editor/PropertiesSidebar'
import { PanelLeft, PanelRight, Server, CheckSquare, AlertCircle, MousePointer2, Route, Eraser, SquareDashed, Ruler, History } from 'lucide-react'
import { mapApiRack } from '../utils/draftUtils'
import { FloorReviewPanel } from '../components/editor/FloorReviewPanel'
import { DraftRecoveryBanner } from '../components/editor/DraftRecoveryBanner'
import { useFloorAccess } from '../hooks/useDataCenterApi'
import { UnplacedRackQueue } from '../components/editor/UnplacedRackQueue'
import { EditorCommandPalette } from '../components/editor/EditorCommandPalette'
import { EditorContextMenu } from '../components/editor/EditorContextMenu'
import { getContextEditorCommands, getEditorCommands, type EditorCommandId } from '../utils/editorCommands.logic'
import { api } from '../utils/api'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { toast } from 'sonner'
// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DataCenterEditorPage() {
  const params = useParams<{ dcCode: string }>()
  const code = params.dcCode ?? ''
  const access = useFloorAccess(code)
  const { activeWorkspaceId } = useWorkspace()
  const stageRef = useRef<any>(null)
  const [leftPanelOpen, setLeftPanelOpen] = useState(true)
  const [rightPanelOpen, setRightPanelOpen] = useState(false)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [inspectionDraft, setInspectionDraft] = useState<{ id: number; revision: number } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const commandTriggerRef = useRef<HTMLElement | null>(null)
  
  const {
    dc, dcQuery, drafts, templates, currentDraftId, setCurrentDraftId,
    handleSave, handlePublish, reviewRequest, closeReview, onReviewPublished, handleDeleteDraft, handleCreateDraft, handleApplyTemplate,
    handleExportJson, handleExportPng
  } = useDataCenterEditor(code, stageRef)

  const { saveStatus, selectedIds, tool, _historyIdx, _history } = useEditorStore()
  useEffect(() => {
    if (selectedIds.length > 0) {
      setRightPanelOpen(true)
      setLeftPanelOpen(false)
    }
  }, [selectedIds])
  const { data: pathwayHealth } = useQuery({
    queryKey: ['trayOccupancy', code, activeWorkspaceId],
    queryFn: async () => (await api.get(`/api/datacenters/${code}/trays/occupancy`, {
      params: { workspace: activeWorkspaceId },
    })).data as {
      status?: string
      cables_seen?: number
      unroutable_cables?: Array<{ cable_id: string }>
    },
    enabled: Boolean(code && dc),
    refetchInterval: 10000,
  })
  const commandContext = {
    canEdit: !!access.data?.edit,
    canPublish: !!currentDraftId && !!access.data?.publish,
    selectedCount: selectedIds.length,
    mode: tool,
    canUndo: _historyIdx > 0,
    canRedo: _historyIdx < _history.length - 1,
    hasClipboard: useEditorStore.getState()._clipboard.length > 0,
  }
  const commands = getEditorCommands(commandContext)
  const contextCommands = getContextEditorCommands(commandContext)

  const closeCommandPalette = useCallback(() => {
    setCommandPaletteOpen(false)
    requestAnimationFrame(() => commandTriggerRef.current?.focus())
  }, [])

  const executeCommand = useCallback((id: EditorCommandId) => {
    const store = useEditorStore.getState()
    const available = getEditorCommands({
      canEdit: !!access.data?.edit,
      canPublish: !!currentDraftId && !!access.data?.publish,
      selectedCount: store.selectedIds.length,
      mode: store.tool,
      canUndo: store._historyIdx > 0,
      canRedo: store._historyIdx < store._history.length - 1,
      hasClipboard: store._clipboard.length > 0,
    }).find(command => command.id === id && command.enabled)
    if (!available) return

    setContextMenu(null)
    switch (id) {
      case 'palette.open':
        commandTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        setCommandPaletteOpen(true)
        break
      case 'tool.select': store.setTool('select'); break
      case 'tool.draw_tray': store.setTool('draw_tray'); break
      case 'tool.draw_zone': store.setTool('draw_zone'); break
      case 'tool.erase': store.setTool('erase'); break
      case 'tool.measure': store.setTool('measure'); break
      case 'history.undo': store.undo(); break
      case 'history.redo': store.redo(); break
      case 'selection.select_all': store.selectAll(); break
      case 'selection.duplicate': store.duplicateSelected(); break
      case 'selection.copy': store.copySelected(); break
      case 'selection.paste': store.pasteClipboard(); break
      case 'selection.delete':
        if (window.confirm(`Удалить ${store.selectedIds.length} объект(ов) из текущего черновика? Публикация плана выполняется отдельно.`)) store.deleteSelected()
        break
      case 'operation.rows': store.setShowRowGenerator(true); break
      case 'operation.setup': store.setShowSetupWizard(true); break
      case 'operation.allocation': store.setShowRackAllocation(true); break
      case 'operation.pathways': store.setShowPathwayGenerator(true); break
      case 'draft.save': if (saveStatus !== 'saving') handleSave(); break
      case 'draft.publish': handlePublish(); break
    }
  }, [access.data?.edit, access.data?.publish, currentDraftId, handleSave, handlePublish, saveStatus])

  const createDraftFromPublished = useCallback(() => {
    if (saveStatus === 'dirty' && !window.confirm('Текущий черновик изменён. Создать новый черновик из опубликованной модели и оставить текущий без изменений?')) return
    void handleCreateDraft()
  }, [handleCreateDraft, saveStatus])

  useEffect(() => {
    document.title = dc ? `${dc.name} — План зала | DC Cable Link` : 'План зала | DC Cable Link'
  }, [dc])

  if (!dc) {
    const msg = dcQuery.isError
      ? `Ошибка: ${(dcQuery.error as any)?.response?.status === 404 ? 'ЦОД не найден' : (dcQuery.error as any)?.message || 'API недоступен'}`
      : 'Загрузка плана зала...';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', flexDirection: 'column', gap: '12px' }}>
        <div>{msg}</div>
        {dcQuery.isError && (
          <a href={`/calculator/${code}`} style={{ color: '#06b6d4', fontSize: '13px' }}>
            → Открыть калькулятор для {code}
          </a>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-app, #0b0e14)', overflow: 'hidden' }}>
      {/* Toolbar */}
      <EditorToolbar
        dcName={dc.name}
        dcCode={code}
        draftName={drafts.find(d => d.id === currentDraftId)?.name}
        onExportJson={handleExportJson}
        onExportPng={handleExportPng}
        onSave={handleSave}
        onPublish={handlePublish}
        onReview={currentDraftId ? () => { void api.get(`/api/datacenters/${code}/drafts/${currentDraftId}`).then(response => setInspectionDraft({ id: response.data.id, revision: response.data.revision })).catch(() => toast.error('Не удалось загрузить сохранённый черновик.')) } : undefined}
        onCommand={executeCommand}
        canPublish={!!currentDraftId && !!access.data?.publish}
        isSaving={saveStatus === 'saving'}
        stageRef={stageRef}
      />
      <DraftRecoveryBanner
        code={code}
        draftId={currentDraftId}
        currentRevision={drafts.find(draft => draft.id === currentDraftId)?.revision ?? null}
      />

      <div className="editor-workspace" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {leftPanelOpen && (
        <aside className="editor-context-dock editor-context-dock-left" aria-label="Панель объектов">
          <UnplacedRackQueue dcCode={code} draftId={currentDraftId} />
          <ObjectPalette />
          <div style={{ height: 1, background: '#1a2744', margin: '4px 0' }} />
          <DraftsSidebar
            drafts={drafts}
            currentDraftId={currentDraftId}
            onSelect={setCurrentDraftId}
            onDelete={handleDeleteDraft}
            onCreate={createDraftFromPublished}
            templates={templates}
            onApply={handleApplyTemplate}
          />
        </aside>
        )}

        {/* Main canvas */}
        <main className="editor-canvas-shell" style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <button
            type="button"
            className="editor-dock-toggle"
            style={{ left: 48, top: 12 }}
            aria-label="История и сравнение ревизий"
            title="История и сравнение ревизий"
            onClick={event => { event.currentTarget.focus(); setHistoryOpen(true) }}
          >
            <History size={15} />
          </button>
          <button
            type="button"
            className={`editor-dock-toggle editor-dock-toggle-left${leftPanelOpen ? ' is-active' : ''}`}
            aria-label={leftPanelOpen ? 'Закрыть панель объектов' : 'Открыть панель объектов'}
            aria-pressed={leftPanelOpen}
            title={leftPanelOpen ? 'Закрыть панель объектов' : 'Открыть панель объектов'}
            onClick={() => { setLeftPanelOpen(open => !open); setRightPanelOpen(false) }}
          >
            <PanelLeft size={15} />
          </button>
          <button
            type="button"
            className={`editor-dock-toggle editor-dock-toggle-right${rightPanelOpen ? ' is-active' : ''}`}
            aria-label={rightPanelOpen ? 'Закрыть инспектор' : 'Открыть инспектор'}
            aria-pressed={rightPanelOpen}
            title={rightPanelOpen ? 'Закрыть инспектор' : 'Открыть инспектор'}
            onClick={() => { setRightPanelOpen(open => !open); setLeftPanelOpen(false) }}
          >
            <PanelRight size={15} />
          </button>
          {pathwayHealth && pathwayHealth.cables_seen && pathwayHealth.status !== 'ok' && (
            <div
              role="status"
              aria-live="polite"
              style={{
                position: 'absolute',
                top: 56,
                left: 48,
                right: 48,
                zIndex: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '9px 12px',
                border: '1px solid var(--status-warning-border, rgba(245, 158, 11, 0.38))',
                borderRadius: 6,
                background: 'var(--status-warning-bg, rgba(120, 72, 10, 0.92))',
                color: 'var(--status-warning-text, #fde68a)',
                fontSize: 12,
                boxShadow: '0 8px 22px rgba(0, 0, 0, 0.2)',
                pointerEvents: 'auto',
              }}
            >
              <span>
                {pathwayHealth.status === 'no_pathway_graph'
                  ? `У ${pathwayHealth.cables_seen} каб. нет опубликованного графа магистралей.`
                  : `Маршрут не рассчитан для ${pathwayHealth.unroutable_cables?.length || 0} из ${pathwayHealth.cables_seen} каб.`}
              </span>
              {access.data?.edit && (
                <button
                  type="button"
                  onClick={() => useEditorStore.getState().setShowPathwayGenerator(true)}
                  style={{
                    flexShrink: 0,
                    border: '1px solid rgba(253, 230, 138, 0.45)',
                    borderRadius: 4,
                    padding: '5px 8px',
                    background: 'rgba(253, 230, 138, 0.12)',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Открыть магистрали
                </button>
              )}
            </div>
          )}
          <DataCenterCanvas
            dcCode={code}
            stageRef={stageRef}
            executeCommand={executeCommand}
            onContextMenu={setContextMenu}
          />
        </main>

        {rightPanelOpen && (
        <aside className="editor-context-dock editor-context-dock-right" aria-label="Инспектор объекта">
          <PropertiesSidebar dc={dc} dcCode={code} draftId={currentDraftId} />
        </aside>
        )}
      </div>

      <EditorCommandPalette commands={commands} open={commandPaletteOpen} onClose={closeCommandPalette} onExecute={executeCommand} />
      {(historyOpen || reviewRequest || inspectionDraft) && <FloorReviewPanel key={reviewRequest || inspectionDraft ? `draft:${(reviewRequest || inspectionDraft)!.id}:${(reviewRequest || inspectionDraft)!.revision}` : 'history'} code={code} hallName={dc?.name} draft={reviewRequest ?? inspectionDraft ?? undefined} onEditObject={(reviewRequest ?? inspectionDraft)?.id === currentDraftId ? (kind, id) => {
        const store = useEditorStore.getState()
        if (kind === 'rack') {
          const addedIndex = id.startsWith('added:') ? Number(id.slice(6)) : null
          const source = addedIndex != null ? drafts.find(item => item.id === currentDraftId)?.racks_json?.added?.[addedIndex] : null
          const key = source ? mapApiRack(source, true).id : Object.keys(store.racks).find(key => String(store.racks[key].dbId) === id)
          if (!key || !store.racks[key]) return false
          store.setSelectedIds([key]); store.setSelectedRowId(null); store.setSelectedEdgeId(null)
        } else if (kind === 'row' && store.rows[id]) {
          store.setSelectedIds([]); store.setSelectedRowId(id); store.setSelectedEdgeId(null); setRightPanelOpen(true)
        } else if (kind === 'pathway_edge' && store.trayEdges[id]) {
          store.setSelectedIds([]); store.setSelectedRowId(null); store.setSelectedEdgeId(id); setRightPanelOpen(true)
        } else return false
        return true
      } : undefined} onOpenDraft={next => { setCurrentDraftId(next.id); setHistoryOpen(false); setInspectionDraft(null); closeReview() }} hasUnsavedChanges={saveStatus === 'dirty' || saveStatus === 'saving' || saveStatus === 'error'} onClose={() => { setHistoryOpen(false); setInspectionDraft(null); closeReview() }} onPublished={onReviewPublished} />}
      {contextMenu && <EditorContextMenu {...contextMenu} commands={contextCommands} onClose={() => setContextMenu(null)} onExecute={executeCommand} />}

      {/* Status bar */}
      <div style={{
        height: 24,
        background: 'var(--bg-deep, #060a0f)',
        borderTop: '1px solid var(--border, #1a2744)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: 16,
        fontSize: 11,
        color: 'var(--text-muted, #4b5563)',
      }}>
        <StatusBar />
      </div>
    </div>
  )
}

function StatusBar() {
  const { tool, selectedIds, racks } = useEditorStore()
  const canUndo = useEditorStore(s => s._historyIdx > 0)
  const dirtyCount = Object.values(racks).filter(r => r.isDirty).length
  const totalRacks = Object.keys(racks).length

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Total objects */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary, #9ca3af)' }}>
          <Server size={11} style={{ color: 'var(--color-accent-blue, #60a5fa)' }} />
          Объектов: <strong style={{ fontFamily: 'var(--font-data)', color: 'var(--text-primary, #f3f4f6)', fontWeight: 600 }}>{totalRacks}</strong>
        </span>

        {/* Selected */}
        {selectedIds.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-accent-blue, #60a5fa)' }}>
            <CheckSquare size={11} />
            Выделено: <strong style={{ fontFamily: 'var(--font-data)', fontWeight: 600 }}>{selectedIds.length}</strong>
          </span>
        )}

        {/* Dirty count */}
        {dirtyCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--status-warning, #f59e0b)' }}>
            <AlertCircle size={11} />
            Изменено: <strong style={{ fontFamily: 'var(--font-data)', fontWeight: 600 }}>{dirtyCount}</strong>
          </span>
        )}

        {/* Active tool badge */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted, #6b7280)', borderLeft: '1px solid var(--border, #1a2744)', paddingLeft: 10 }}>
          {tool === 'select' && <MousePointer2 size={11} />}
          {tool === 'draw_tray' && <Route size={11} />}
          {tool === 'draw_zone' && <SquareDashed size={11} />}
          {tool === 'erase' && <Eraser size={11} />}
          {tool === 'measure' && <Ruler size={11} />}
          Инструмент: <span style={{ color: 'var(--text-secondary, #9ca3af)', fontWeight: 500 }}>{tool === 'select' ? 'Выбор (V)' : tool === 'draw_tray' ? 'Трасса (D)' : tool === 'draw_zone' ? 'Зона (Z)' : tool === 'measure' ? 'Измерение (L)' : 'Ластик (E)'}</span>
        </span>
      </div>

      {/* Shortcuts bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted, #6b7280)', fontSize: 10 }}>
        <span style={{ opacity: canUndo ? 1 : 0.5 }}>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+Z</kbd> Отменить
        </span>
        <span>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+C</kbd> / <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+V</kbd> Буфер
        </span>
        <span>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+D</kbd> Дубликат
        </span>
        <span>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Del</kbd> Удалить
        </span>
      </div>
    </div>
  )
}
